import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Alert, Snackbar } from '@mui/material';
import { GoogleLogin, CredentialResponse, useGoogleOneTapLogin } from '@react-oauth/google';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import DashboardPage from './pages/DashboardPage';
import ImportPage from './pages/ImportPage';
import TransactionsPage from './pages/TransactionsPage';
import BudgetPage from './pages/BudgetPage';
import ReportsPage from './pages/ReportsPage';
import CategoriesPage from './pages/CategoriesPage';
import AccountsPage from './pages/AccountsPage';
import CardsPage from './pages/CardsPage';
import ReviewPage from './pages/ReviewPage';
import SettingsPage from './pages/SettingsPage';
import AIAnalysisPage from './pages/AIAnalysisPage';
import RulesPage from './pages/RulesPage';

export default function App() {
  const { auth, loading, error, login, logout, needsRefresh } = useAuth();

  const handleGoogleSuccess = (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      login(credentialResponse.credential);
    }
  };

  // Use One Tap for automatic silent refresh when token is about to expire
  useGoogleOneTapLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      console.log('One Tap login failed - may need manual re-auth');
    },
    // Only prompt when user is logged in and needs refresh
    disabled: !auth || !needsRefresh,
    cancel_on_tap_outside: false,
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!auth) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <Typography variant="h5">HomeLedger</Typography>
        <Typography color="text.secondary">Kirjaudu sisään jatkaaksesi</Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => {}}
          useOneTap
          theme="filled_blue"
          text="signin_with"
          shape="rectangular"
        />
      </Box>
    );
  }

  if (!auth.allowed) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <Typography variant="h5">Pääsy estetty</Typography>
        <Typography color="text.secondary">
          {auth.email} ei ole sallittujen käyttäjien listalla.
        </Typography>
        <Button variant="outlined" onClick={logout}>
          Kirjaudu ulos
        </Button>
      </Box>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/cards" element={<CardsPage />} />
        <Route path="/rules" element={<RulesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/ai" element={<AIAnalysisPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Token refresh notification */}
      <Snackbar
        open={needsRefresh}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Istunto vanhenemassa. Kirjaudu uudelleen klikkaamalla Google-kuvaketta."
      />
    </Layout>
  );
}
